import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { User, Lock, Mail, Shield, Phone, MapPin, Save, Eye, EyeOff, FileText, Briefcase, CheckCircle, Download, Edit3, XCircle } from 'lucide-react';
import { RootState } from '../store/reducers';
import { updateProfileRequest, changePasswordRequest } from '../store/auth/actions';
import { fetchUserStats } from '../store/users/service';
import { UserStats } from '../store/users/reducer';
import './Profile.css';

const Profile: React.FC = () => {
  const dispatch = useDispatch();
  const { user, isUpdating } = useSelector((state: RootState) => state.auth);

  // Profile form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  // User stats
  const [myStats, setMyStats] = useState<UserStats | null>(null);

  // Password form
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPwd, setShowOldPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [pwdError, setPwdError] = useState('');

  // Sync form with user data
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setPhone(user.phone || '');
      setAddress(user.address || '');
    }
  }, [user]);

  // Fetch user stats
  useEffect(() => {
    if (user?.id) {
      fetchUserStats(user.id).then(res => setMyStats(res.data)).catch(() => {});
    }
  }, [user?.id]);

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(updateProfileRequest({
      first_name: firstName,
      last_name: lastName,
      phone,
      address,
    }));
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError('');

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPwdError('Tous les champs sont requis.');
      return;
    }
    if (newPassword.length < 6) {
      setPwdError('Le nouveau mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (oldPassword === newPassword) {
      setPwdError('Le nouveau mot de passe doit être différent de l\'ancien.');
      return;
    }

    dispatch(changePasswordRequest(
      { old_password: oldPassword, new_password: newPassword },
      () => {
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    ));
  };

  const roles = (user?.role || []).map((r: any) => r.name);
  const roleLabel = roles.includes('admin') || roles.includes('superadmin')
    ? 'Administrateur'
    : roles.includes('validateur')
      ? 'Validateur'
      : roles.includes('agent')
        ? 'Agent CNDJ'
        : 'Utilisateur';

  const initials = user?.first_name && user?.last_name
    ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
    : (user?.email || 'U').charAt(0).toUpperCase();

  return (
    <div className="profile-page">
      {/* Profile header */}
      <div className="profile-header-card">
        <div className="profile-avatar-large">{initials}</div>
        <div className="profile-header-info">
          <h2 className="profile-header-name">
            {user?.first_name && user?.last_name
              ? `${user.first_name} ${user.last_name}`
              : user?.email || 'Utilisateur'}
          </h2>
          <span className="profile-header-role">
            <Shield size={14} /> {roleLabel}
          </span>
          <span className="profile-header-email">
            <Mail size={14} /> {user?.email}
          </span>
          {user?.created_at && (
            <span className="profile-header-date">
              Membre depuis le {new Date(user.created_at).toLocaleDateString('fr-FR')}
            </span>
          )}
        </div>
      </div>

      {/* Stats section */}
      {myStats && (
        <div className="profile-stats-card">
          <h3 className="profile-stats-title">Mes statistiques</h3>
          <div className="profile-stats-grid">
            <div className="profile-stat-item">
              <div className="profile-stat-icon" style={{ background: '#EFF6FF', color: '#2563EB' }}><FileText size={18} /></div>
              <span className="profile-stat-value">{myStats.decisions_count}</span>
              <span className="profile-stat-label">Décisions assignées</span>
            </div>
            <div className="profile-stat-item">
              <div className="profile-stat-icon" style={{ background: '#FFF7ED', color: '#D97706' }}><Briefcase size={18} /></div>
              <span className="profile-stat-value">{myStats.charge}</span>
              <span className="profile-stat-label">En charge</span>
            </div>
            <div className="profile-stat-item">
              <div className="profile-stat-icon" style={{ background: '#ECFDF5', color: '#059669' }}><CheckCircle size={18} /></div>
              <span className="profile-stat-value">{myStats.nombre_traitees}</span>
              <span className="profile-stat-label">Traitées</span>
            </div>
            <div className="profile-stat-item">
              <div className="profile-stat-icon" style={{ background: '#D1FAE5', color: '#047857' }}><Download size={18} /></div>
              <span className="profile-stat-value">{myStats.publiees}</span>
              <span className="profile-stat-label">Publiées</span>
            </div>
            <div className="profile-stat-item">
              <div className="profile-stat-icon" style={{ background: '#F3F4F6', color: '#6B7280' }}><Edit3 size={18} /></div>
              <span className="profile-stat-value">{myStats.en_brouillon}</span>
              <span className="profile-stat-label">En brouillon</span>
            </div>
            <div className="profile-stat-item">
              <div className="profile-stat-icon" style={{ background: '#FEF2F2', color: '#DC2626' }}><XCircle size={18} /></div>
              <span className="profile-stat-value">{myStats.rejetees}</span>
              <span className="profile-stat-label">Rejetées</span>
            </div>
          </div>
        </div>
      )}

      <div className="profile-grid">
        {/* Section 1: Personal info */}
        <div className="profile-card">
          <div className="profile-card-header">
            <User size={18} />
            <h3>Informations personnelles</h3>
          </div>
          <form onSubmit={handleProfileSubmit} className="profile-form">
            <div className="profile-form-row">
              <div className="profile-field">
                <label>Prénom</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Votre prénom"
                />
              </div>
              <div className="profile-field">
                <label>Nom</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Votre nom"
                />
              </div>
            </div>

            <div className="profile-field">
              <label><Mail size={14} /> Email</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="profile-field-disabled"
              />
              <span className="profile-field-hint">L'email ne peut pas être modifié.</span>
            </div>

            <div className="profile-form-row">
              <div className="profile-field">
                <label><Phone size={14} /> Téléphone</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+225 07 00 00 00 00"
                />
              </div>
              <div className="profile-field">
                <label><MapPin size={14} /> Adresse</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Votre adresse"
                />
              </div>
            </div>

            <div className="profile-form-actions">
              <button type="submit" className="btn-profile-save" disabled={isUpdating}>
                <Save size={14} /> {isUpdating ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </form>
        </div>

        {/* Section 2: Change password */}
        <div className="profile-card">
          <div className="profile-card-header">
            <Lock size={18} />
            <h3>Changer le mot de passe</h3>
          </div>
          <form onSubmit={handlePasswordSubmit} className="profile-form">
            <div className="profile-field">
              <label>Mot de passe actuel</label>
              <div className="profile-password-wrapper">
                <input
                  type={showOldPwd ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Entrez votre mot de passe actuel"
                />
                <button
                  type="button"
                  className="profile-pwd-toggle"
                  onClick={() => setShowOldPwd(!showOldPwd)}
                >
                  {showOldPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="profile-field">
              <label>Nouveau mot de passe</label>
              <div className="profile-password-wrapper">
                <input
                  type={showNewPwd ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Entrez le nouveau mot de passe"
                />
                <button
                  type="button"
                  className="profile-pwd-toggle"
                  onClick={() => setShowNewPwd(!showNewPwd)}
                >
                  {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="profile-field">
              <label>Confirmer le nouveau mot de passe</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmez le nouveau mot de passe"
              />
            </div>

            {pwdError && (
              <div className="profile-pwd-error">{pwdError}</div>
            )}

            <div className="profile-form-actions">
              <button type="submit" className="btn-profile-password" disabled={isUpdating}>
                <Lock size={14} /> {isUpdating ? 'En cours...' : 'Changer le mot de passe'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
